import React, { useState, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useStrings } from '@/hooks/useStrings';

// CycleSetupNudge, a soft prompt on /home that invites users who
// have started training but never logged a cycle to set one up. The
// prompt is dismissable; once dismissed it never reappears (sentinel
// in localStorage). Renders only when:
//   - the user has trained at least once
//   - the user has no cycle data set up
//   - the user has not already dismissed this nudge
//
// Lives between the session card and the reflection card so it's
// noticeable but doesn't crowd today's main practice action.

const STORAGE_KEY = 'bb_cycle_nudge_dismissed';

function isDismissed() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markDismissed() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
  } catch {}
}

const styles = {
  card: {
    background: 'color-mix(in srgb, var(--primary, #A8003F) 4%, var(--surface))',
    border: '1px solid color-mix(in srgb, var(--primary, #A8003F) 16%, transparent)',
    borderRadius: 16,
    padding: '16px 18px',
    margin: '0 0 16px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  closeBtn: {
    position: 'absolute',
    top: 6,
    right: 8,
    width: 32, height: 32,
    border: 'none',
    background: 'transparent',
    color: 'var(--warm-brown)',
    cursor: 'pointer',
    fontSize: 20,
    lineHeight: 1,
    WebkitTapHighlightColor: 'transparent',
    borderRadius: 999,
  },
  eyebrow: {
    fontFamily: 'var(--font-ui)',
    fontSize: 11, fontWeight: 700,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--primary, #A8003F)',
  },
  body: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    lineHeight: 1.5,
    color: 'var(--dark-brown)',
    margin: 0,
    paddingRight: 24,
  },
  ctaRow: {
    display: 'flex',
    gap: 8,
    marginTop: 4,
  },
  primaryBtn: {
    flex: 1,
    minHeight: 40,
    background: 'var(--primary, #A8003F)',
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    fontFamily: 'var(--font-ui)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.04em',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
};

export default function CycleSetupNudge({ onSetUp }) {
  const { userProgress } = useApp();
  const t = useStrings();
  const [hidden, setHidden] = useState(false);

  // Read once on mount; the localStorage check doesn't need to be
  // reactive (the user can only dismiss this card from this card).
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => { setDismissed(isDismissed()); }, []);

  const hasTrained = (userProgress?.total_sessions_completed || 0) >= 1;
  const hasCycle = Boolean(userProgress?.cycle_last_period_start);

  if (!hasTrained || hasCycle || dismissed || hidden) return null;

  function handleDismiss() {
    markDismissed();
    setHidden(true);
  }

  function handleSetUp() {
    markDismissed();
    setHidden(true);
    onSetUp?.();
  }

  return (
    <div style={styles.card} role="status">
      <button type="button" onClick={handleDismiss} style={styles.closeBtn} aria-label={t('cycle_nudge_dismiss')}>
        ×
      </button>
      <div style={styles.eyebrow}>{t('cycle_nudge_eyebrow')}</div>
      <p style={styles.body}>{t('cycle_nudge_body')}</p>
      <div style={styles.ctaRow}>
        <button type="button" onClick={handleSetUp} style={styles.primaryBtn}>
          {t('cycle_nudge_cta')}
        </button>
      </div>
    </div>
  );
}
